import { USD, int, magnitude, pct } from '../lib/format';
import { C, ink } from '../lib/theme';
import type { Filters, Reason } from '../lib/types';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useEventList } from '../lib/useEventList';
import { EventList } from '../components/EventList';
import { KpiCard } from '../components/KpiCard';
import { IconCircleDashed, IconFlag, IconShieldCheck, IconSum, IconTimer, IconUndo } from '../components/icons';
import { ChartCard } from '../components/charts/ChartCard';
import { WaterfallChart } from '../components/charts/WaterfallChart';
import { EmptyState, SectionHeader } from '../components/primitives';
import type { EventCardHandlers } from '../components/EventCard';
import { useToast } from '../components/Toast';
import { useFilters } from '../lib/useFilters';

/**
 * Aba Confirmados (PRD §4) — só `real_lost`, é a visão de "receita confirmada"
 * pra liderança. Índice local: 01 Resumo · 02 Lista · 03 Bruto vs Confirmado.
 */
export function ConfirmadosPage({ filters, reasons, handlers }: {
  filters: Filters;
  reasons: Reason[];
  handlers: Omit<EventCardHandlers, 'onAudit' | 'onPickReason' | 'onSetCorrection' | 'onClearCorrection'>;
}) {
  const toast = useToast();
  const { clearAll } = useFilters();
  const key = JSON.stringify(filters);
  const kpis = useAsync(() => api.confirmadosKpis(filters), [key]);
  const waterfall = useAsync(() => api.waterfall({ ...filters, audit_status: 'real_lost' }), [key]);
  const list = useEventList(filters, 'confirmados', reasons);

  const k = kpis.data;
  const slaPct = int(k?.auditados_com_data) > 0
    ? (int(k?.auditados_no_prazo) / int(k?.auditados_com_data)) * 100
    : 0;

  const fullHandlers: EventCardHandlers = {
    ...handlers,
    onAudit: (ev) => list.toggleAudit(ev, (msg) => toast(msg, 'error')),
    onPickReason: (ev, picked) => {
      const reason = picked[0] ?? null;
      list.setReason(ev, picked, (msg) => toast(msg, 'error'));
      if (reason?.is_exception) {
        toast('Esse evento vai ser excluído do total de Perda Confirmada.', 'info');
      }
    },
    onSetCorrection: (ev, body) => list.setCorrection(ev, body, (msg) => toast(msg, 'error')),
    onClearCorrection: (ev) => list.clearCorrection(ev, (msg) => toast(msg, 'error')),
  };

  return (
    <>
      <section style={{ marginBottom: 34 }}>
        <SectionHeader id="resumo" title="Resumo confirmado" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 16 }}>
          <KpiCard
            icon={<IconSum />} label="Total" loading={kpis.loading}
            formula="SUM(mrr_lost) BRUTO de tudo marcado como real_lost — tudo que passou por auditoria"
            value={magnitude(k?.total_bruto)} format={USD}
            sub={`· ${int(k?.total_eventos)} eventos auditados`}
            color={C.churn} glow="0 0 26px rgba(242,85,90,0.35)" chipBg="rgba(242,85,90,0.16)"
          />
          <KpiCard
            icon={<IconShieldCheck />} label="Perda Confirmada" loading={kpis.loading}
            formula="Total menos Anulado menos VDI — é o número de receita real perdida"
            value={magnitude(k?.perda_confirmada)} format={USD}
            sub="receita real perdida"
            color={C.accent} glow="0 0 26px rgba(255,144,112,0.32)" chipBg="rgba(255,144,112,0.16)"
          />
          <KpiCard
            icon={<IconUndo />} label="Anulado" loading={kpis.loading}
            formula="SUM(mrr_lost) bruto dos eventos corrigidos pra zero na tela de Evidência — perda que não existe"
            value={magnitude(k?.anulado_valor)} format={USD}
            sub={`${int(k?.anulado_count)} corrigido${int(k?.anulado_count) === 1 ? '' : 's'}`}
            color={C.green} chipBg="rgba(46,204,113,0.16)"
          />
          {/* VDI — motivo de regra sinalizado mas SEM correção manual ainda:
              valor "pendurado", não resolvido. Azul de propósito (pedido
              explícito: sem cinza, sem parecer com Anulado) — não é perda,
              mas também não está formalmente anulado, é um alerta de
              revisão pendente. Mesmo ícone (bandeira) e cor do chip do
              Funil — reconhecível como o mesmo conceito em toda a tela. */}
          <KpiCard
            icon={<IconFlag />} label="VDI" loading={kpis.loading}
            formula="SUM(mrr_lost) bruto dos eventos com motivo de regra ainda SEM correção — Índice de Inconsistência"
            value={magnitude(k?.vdi_valor)} format={USD}
            sub={`${int(k?.vdi_count)} pendente${int(k?.vdi_count) === 1 ? '' : 's'} de revisão`}
            color={C.blueSoft} chipBg="rgba(74,125,255,0.16)"
          />
          <KpiCard
            icon={<IconTimer />} label="Auditado no prazo" loading={kpis.loading}
            formula="% dos confirmados auditados em até 2 dias desde a criação do evento"
            value={slaPct} format={(n) => pct(n)}
            sub={`${int(k?.auditados_no_prazo)} de ${int(k?.auditados_com_data)}`}
            color={slaPct >= 70 ? C.green : C.amber}
            chipBg={slaPct >= 70 ? 'rgba(46,204,113,0.16)' : 'rgba(245,165,36,0.16)'}
          />
        </div>
      </section>

      <section style={{ marginBottom: 34 }}>
        <SectionHeader id="lista" title="Lista de eventos confirmados" />
        <EventList
          items={list.items} loading={list.loading} loadingMore={list.loadingMore}
          error={list.error} hasMore={list.hasMore} busyIds={list.busyIds}
          variant="confirmados" reasons={reasons} filters={filters} handlers={fullHandlers}
          onLoadMore={list.loadMore} onRetry={list.reload}
          empty={
            <EmptyState
              tone="neutral" icon={<IconCircleDashed size={22} />} title="Nada confirmado ainda"
              text="Nenhum evento bate com os filtros atuais. Eventos aparecem aqui depois de auditados na Fila."
              action={
                <button type="button" onClick={clearAll} style={{
                  marginTop: 6, border: `1px solid ${ink(0.18)}`, background: ink(0.06), color: C.fg,
                  cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 650,
                  padding: '8px 16px', borderRadius: 10,
                }}>Limpar filtros</button>
              }
            />
          }
        />
      </section>

      <section id="waterfall" style={{ scrollMarginTop: 150 }}>
        <SectionHeader title="Bruto vs Confirmado" />
        <ChartCard
          title="Bruto → Anulado → VDI → Confirmada"
          loading={waterfall.loading} error={waterfall.error} onRetry={waterfall.reload} height={280}
        >
          <WaterfallChart steps={waterfall.data ?? []} />
        </ChartCard>
      </section>
    </>
  );
}
